// Audit trail — shared by the platform view (Global Audit) and each tenant's own view.
// Data: GET /api/audit (platform admins, every tenant) or /api/audit/tenant (a tenant's own).
import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../utils/api";

export const ACTOR_KIND = {
    "platform-admin": { label: "Platform admin", tone: "indication" },
    user: { label: "User", tone: "information" },
    customer: { label: "Customer login", tone: "positive" },
    system: { label: "System", tone: "neutral" },
};
export const PERIODS = [["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["all", "All"]];
const DAY = 86400000;

const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); };
export const inPeriod = (e, p) => {
    const t = Date.parse(e.at || 0);
    return p === "all" || (p === "today" ? t >= startOfToday() : t >= Date.now() - (p === "7d" ? 7 : 30) * DAY);
};

/** "Lucas Arruda" + "updated ticket CS0001003: Status New → In progress" */
export const actorName = e => e.actor?.name || e.actor?.email || "System";

export function relTime(iso) {
    if (!iso) return "—";
    const s = (Date.now() - Date.parse(iso)) / 1000;
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)} min ago`;
    if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
    if (s < 7 * 86400) return `${Math.floor(s / 86400)} d ago`;
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}
export const fullTime = iso => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "medium" }) : "—");

/** Loads events (newest first) with "load older" paging */
export function useAuditEvents(endpoint, params = {}) {
    const [data, setData] = useState({ events: [], tenants: {}, categories: {} });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [more, setMore] = useState(true);
    const key = JSON.stringify(params);

    const load = useCallback(async (before) => {
        setLoading(true);
        try {
            const { data: d } = await api.get(endpoint, { params: { ...JSON.parse(key), limit: 300, ...(before ? { before } : {}) } });
            setData(prev => ({
                ...d, tenants: d.tenants || prev.tenants || {}, categories: d.categories || prev.categories || {},
                events: before ? [...prev.events, ...d.events.filter(e => !prev.events.some(p => p.id === e.id))] : d.events,
            }));
            setMore((d.events || []).filter(e => e.source !== "legacy").length >= 300);
            setError("");
        } catch (err) {
            setError(err?.response?.data?.error || "Could not load the audit trail.");
        } finally {
            setLoading(false);
        }
    }, [endpoint, key]);

    useEffect(() => { load(); }, [load]);
    const oldest = useMemo(() => data.events.filter(e => e.atMs).reduce((m, e) => Math.min(m, e.atMs), Infinity), [data.events]);
    return { ...data, loading, error, reload: () => load(), loadOlder: more && Number.isFinite(oldest) ? () => load(oldest) : null };
}

/** Search + filters applied in the browser */
export function filterEvents(events, { q = "", period = "30d", category = "", severity = "", kind = "", tenant = "" }) {
    const s = q.trim().toLowerCase();
    return events.filter(e => inPeriod(e, period)
        && (!category || e.category === category) && (!severity || e.severity === severity)
        && (!kind || e.actor?.kind === kind) && (!tenant || e.tenantId === tenant)
        && (!s || [actorName(e), e.actor?.email, e.summary, e.action, e.entity?.label, e.tenantId, ...(e.changes || []).flatMap(c => [c.field, c.from, c.to])]
            .some(v => String(v || "").toLowerCase().includes(s))));
}

export function downloadCsv(events, { tenants = {}, file = "audit" } = {}) {
    const rows = [["When (UTC)", "Tenant", "Who", "Email", "Role", "Kind", "Did", "Category", "Severity", "Changes", "Record", "IP", "Event id"],
        ...events.map(e => [e.at || "", tenants[e.tenantId] || e.tenantId || "", actorName(e), e.actor?.email || "", e.actor?.role || "", e.actor?.kind || "",
            e.summary || e.action, e.category || "", e.severity || "", (e.changes || []).map(c => `${c.field}: ${c.from} → ${c.to}`).join("; "),
            e.entity?.label || e.entity?.id || "", e.ip || "", e.id])];
    const csv = rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    a.download = `${file}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
}
